/**
 * MariaDbPerfPanel — výkonnostní monitoring MariaDB instance.
 *
 * Záložky: Přehled | Procesy | Tabulky | Konfigurace | EXPLAIN
 *
 * Props:
 *   instance  — název DB instance (dbhost_instances.name)
 *   targetNode — volitelný ACM node
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { mcpCall } from '../lib/mcp';
import { Tab, TabList, TabPanel, Tabs } from './ui/Tabs';
import { Select } from './ui/Select';
import { Card, CardContent } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Cpu,
  Database,
  HardDrive,
  Loader2,
  Network,
  Play,
  RefreshCw,
  Settings,
  Terminal,
  XCircle,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { themedConfirm } from '../lib/themed-dialogs';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MariaStatus {
  ok: boolean;
  instance: string;
  uptime_seconds: number;
  uptime_formatted: string;
  version: string;
  questions_total: number;
  qps: number;
  select_per_s: number;
  insert_per_s: number;
  update_per_s: number;
  delete_per_s: number;
  connections: {
    current: number;
    running: number;
    cached: number;
    max_used: number;
    total: number;
    aborted: number;
    max_allowed: number;
  };
  innodb: {
    buffer_pool_size: string;
    buffer_pool_pages_total: number;
    buffer_pool_pages_used: number;
    buffer_pool_pages_free: number;
    buffer_pool_pages_dirty: number;
    buffer_pool_used_pct: number;
    buffer_pool_hit_rate: number;
    row_reads: number;
    row_inserts: number;
    row_updates: number;
    row_deletes: number;
    os_reads: number;
    os_writes: number;
    log_writes: number;
  };
  query_cache: {
    enabled: boolean;
    size: string;
    hits: number;
    inserts: number;
    not_cached: number;
  };
  key_cache_hit_rate: number;
  slow_queries: number;
  slow_query_log: boolean;
  long_query_time: number;
  table_locks_waited: number;
  tmp_disk_tables: number;
  tmp_tables: number;
  open_files: number;
  open_tables: number;
  bytes_received_formatted: string;
  bytes_sent_formatted: string;
}

interface MariaProcess {
  id: number;
  user: string;
  host: string;
  db: string | null;
  command: string;
  time: number;
  state: string;
  info: string | null;
}

interface MariaTable {
  schema: string;
  table: string;
  engine: string;
  rows: number;
  data_length: number;
  index_length: number;
  total_length: number;
  data_fmt: string;
  index_fmt: string;
  total_fmt: string;
}

interface ExplainStep {
  id: number;
  select_type: string;
  table: string;
  type: string;
  possible_keys: string | null;
  key: string | null;
  key_len: string | null;
  ref: string | null;
  rows: number;
  filtered: number;
  extra: string;
  quality: 'good' | 'ok' | 'warn' | 'bad';
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function pct(used: number, total: number) {
  return total > 0 ? Math.round((used / total) * 100) : 0;
}

function connPct(inst: MariaStatus) {
  return pct(inst.connections.current, inst.connections.max_allowed);
}

function ProgressBar(
  { value, max, colorClass = 'bg-blue-500' }: { value: number; max: number; colorClass?: string },
) {
  const p = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className='flex items-center gap-2'>
      <div className='flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden'>
        <div
          className={`h-full rounded-full transition-all ${colorClass}`}
          style={{ width: `${p}%` }}
        />
      </div>
      <span className='text-xs text-gray-500 w-9 text-right'>{p}%</span>
    </div>
  );
}

function MetricCard({ label, value, sub, icon: Icon, color = 'text-gray-400', warn = false }: {
  label: string;
  value: string | number;
  sub?: string;
  icon?: React.ComponentType<{ size?: number | string; className?: string }>;
  color?: string;
  warn?: boolean;
}) {
  return (
    <div
      className={`bg-gray-900/40 border rounded-xl p-3 ${
        warn ? 'border-amber-500/30' : 'border-gray-800'
      }`}
    >
      <div className='flex items-center gap-1.5 mb-1'>
        {Icon && <Icon size={11} className={warn ? 'text-amber-400' : color} />}
        <span className='text-[10px] text-gray-600 uppercase tracking-wider'>{label}</span>
      </div>
      <p className={`text-xl font-bold ${warn ? 'text-amber-300' : 'text-white'}`}>{value}</p>
      {sub && <p className='text-[10px] text-gray-600 mt-0.5'>{sub}</p>}
    </div>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────────

function OverviewPanel({ instance, targetNode }: { instance: string; targetNode?: string }) {
  const { t, i18n } = useTranslation();
  const extra = targetNode ? { target_node: targetNode } : {};
  const [autoRefresh, setAutoRefresh] = useState(true);

  const { data, isLoading, refetch: refresh, dataUpdatedAt } = useQuery({
    queryKey: ['mariadb-status', instance, targetNode],
    queryFn: () => mcpCall<MariaStatus>('dbhost.mariadb_status', { instance, ...extra }),
    refetchInterval: autoRefresh ? 10_000 : false,
  });

  const s = data;

  const lastUpdate = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString(i18n.language)
    : null;

  if (isLoading) {
    return (
      <div className='py-12 text-center text-gray-500'>
        <Loader2 size={16} className='animate-spin inline mr-2' />
        {t('dbhost.loadingMetrics', 'Načítám metriky...')}
      </div>
    );
  }

  if (!s?.ok) {
    return (
      <div className='py-8 text-center'>
        <XCircle size={24} className='text-red-400 mx-auto mb-2' />
        <p className='text-sm text-gray-400'>
          {(s as unknown as { error?: string })?.error ?? t('dbhost.loadFailed', 'Načtení selhalo')}
        </p>
      </div>
    );
  }

  const bpHitOk = s.innodb.buffer_pool_hit_rate >= 99;
  const slowOk = s.slow_queries === 0;
  const lockOk = s.table_locks_waited === 0;
  const connPc = connPct(s);

  return (
    <div className='space-y-4 mt-2'>
      {/* Toolbar */}
      <div className='flex items-center gap-2'>
        <span className='text-xs text-gray-600'>
          {lastUpdate && t('dbhost.updatedAt', 'Aktualizováno: {{time}}', { time: lastUpdate })}
        </span>
        <div className='ml-auto flex items-center gap-2'>
          <button
            type='button'
            onClick={() => setAutoRefresh((a) => !a)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs transition-colors ${
              autoRefresh
                ? 'bg-blue-600/20 text-blue-400 border-blue-500/30'
                : 'bg-gray-800 text-gray-500 border-gray-700'
            }`}
          >
            <Activity size={11} />{t('dbhost.autoRefresh10s', 'Auto-refresh 10s')}
          </button>
          <Button variant='ghost' size='icon' onClick={() => refresh()}>
            <RefreshCw size={13} />
          </Button>
        </div>
      </div>

      {/* Server info */}
      <div className='flex items-center gap-3 text-xs text-gray-500'>
        <span className='font-mono text-white'>{s.version}</span>
        <span>up {s.uptime_formatted}</span>
        <span>
          {t('dbhost.questionsTotal', '{{count}} dotazů celkem', { count: s.questions_total.toLocaleString() })}
        </span>
      </div>

      {/* Key metrics */}
      <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
        <MetricCard
          label='QPS'
          value={s.qps}
          sub={t('dbhost.qpsSub', 'dotazů/s')}
          icon={Zap}
          color='text-blue-400'
        />
        <MetricCard
          label='Connections'
          value={s.connections.current}
          sub={t('dbhost.maxUsed', 'max použito: {{used}} / {{max}}', {
            used: s.connections.max_used,
            max: s.connections.max_allowed,
          })}
          icon={Network}
          warn={connPc > 80}
        />
        <MetricCard
          label='Running'
          value={s.connections.running}
          sub={t('dbhost.activeThreads', 'aktivních vláken')}
          icon={Cpu}
          warn={s.connections.running > 20}
        />
        <MetricCard
          label='Slow queries'
          value={s.slow_queries}
          sub={`limit: ${s.long_query_time}s`}
          icon={Clock}
          warn={!slowOk}
        />
      </div>

      <div className='grid grid-cols-2 gap-4'>
        {/* InnoDB buffer pool */}
        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 mb-3'>
              <HardDrive size={13} className='text-blue-400' />
              <h4 className='text-sm font-semibold text-white'>InnoDB Buffer Pool</h4>
              <Badge variant={bpHitOk ? 'green' : 'yellow'} className='ml-auto'>
                {s.innodb.buffer_pool_hit_rate}% hit rate
              </Badge>
            </div>
            <div className='space-y-2 text-xs'>
              <div className='flex justify-between text-gray-500 mb-1'>
                <span>
                  {t('dbhost.poolUsed', 'Obsazeno ({{size}})', { size: s.innodb.buffer_pool_size })}
                </span>
                <span>
                  {t('dbhost.pagesCount', '{{used}} / {{total}} stránek', {
                    used: s.innodb.buffer_pool_pages_used.toLocaleString(),
                    total: s.innodb.buffer_pool_pages_total.toLocaleString(),
                  })}
                </span>
              </div>
              <ProgressBar
                value={s.innodb.buffer_pool_pages_used}
                max={s.innodb.buffer_pool_pages_total}
                colorClass={s.innodb.buffer_pool_used_pct > 90 ? 'bg-amber-500' : 'bg-blue-500'}
              />
              {s.innodb.buffer_pool_pages_dirty > 0 && (
                <div className='flex justify-between text-gray-600'>
                  <span>Dirty pages</span>
                  <span>{s.innodb.buffer_pool_pages_dirty.toLocaleString()}</span>
                </div>
              )}
              <div className='grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-800'>
                <div className='text-gray-600'>
                  Rows read/s<div className='text-white font-mono'>
                    {uptime > 0 ? (s.innodb.row_reads / s.uptime_seconds).toFixed(0) : '—'}
                  </div>
                </div>
                <div className='text-gray-600'>
                  OS reads<div className='text-white font-mono'>
                    {s.innodb.os_reads.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Connections + Operations */}
        <Card>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 mb-3'>
              <Network size={13} className='text-emerald-400' />
              <h4 className='text-sm font-semibold text-white'>Connections</h4>
            </div>
            <div className='space-y-2'>
              <div className='flex justify-between text-xs text-gray-500 mb-1'>
                <span>{t('dbhost.currentMax', 'Aktuální / Max')}</span>
                <span>{s.connections.current} / {s.connections.max_allowed}</span>
              </div>
              <ProgressBar
                value={s.connections.current}
                max={s.connections.max_allowed}
                colorClass={connPc > 80
                  ? 'bg-red-500'
                  : connPc > 60
                  ? 'bg-amber-500'
                  : 'bg-emerald-500'}
              />
              <div className='grid grid-cols-2 gap-x-4 gap-y-1 mt-2 pt-2 border-t border-gray-800 text-xs'>
                {[
                  ['SELECT/s', s.select_per_s],
                  ['INSERT/s', s.insert_per_s],
                  ['UPDATE/s', s.update_per_s],
                  ['DELETE/s', s.delete_per_s],
                ].map(([label, val]) => (
                  <div key={label as string} className='flex justify-between'>
                    <span className='text-gray-600'>{label}</span>
                    <span className='text-white font-mono'>{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Warning flags */}
      {(!slowOk || !lockOk || s.tmp_disk_tables > 0 || !bpHitOk) && (
        <div className='space-y-2'>
          <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wider'>
            {t('dbhost.warnings', 'Upozornění')}
          </h4>
          {!bpHitOk && (
            <WarnRow icon={HardDrive}>
              {t('dbhost.warnHitRate', 'InnoDB buffer pool hit rate {{rate}}% — zvažte zvýšení', {
                rate: s.innodb.buffer_pool_hit_rate,
              })}{' '}
              <code>innodb_buffer_pool_size</code>
            </WarnRow>
          )}
          {!slowOk && (
            <WarnRow icon={Clock}>
              {t('dbhost.warnSlowQueries', '{{count}} pomalých dotazů (limit: {{limit}}s)', {
                count: s.slow_queries,
                limit: s.long_query_time,
              })}
              {!s.slow_query_log && t('dbhost.warnSlowLogOff', ' — slow query log je vypnutý')}
            </WarnRow>
          )}
          {!lockOk && (
            <WarnRow icon={AlertTriangle}>
              {t(
                'dbhost.warnTableLocks',
                '{{count}} table lock čekání — zvažte přechod na InnoDB nebo optimalizaci dotazů',
                { count: s.table_locks_waited },
              )}
            </WarnRow>
          )}
          {s.tmp_disk_tables > 0 && (
            <WarnRow icon={HardDrive}>
              {t(
                'dbhost.warnTmpDisk',
                '{{count}} dočasných tabulek na disku — zvažte zvýšení',
                { count: s.tmp_disk_tables },
              )}{' '}
              <code>tmp_table_size</code>
            </WarnRow>
          )}
        </div>
      )}

      {/* Network */}
      <div className='flex gap-4 text-xs text-gray-500 pt-1'>
        <span>
          ↓ {t('dbhost.received', 'Přijato:')}{' '}
          <span className='text-white'>{s.bytes_received_formatted}</span>
        </span>
        <span>
          ↑ {t('dbhost.sent', 'Odesláno:')}{' '}
          <span className='text-white'>{s.bytes_sent_formatted}</span>
        </span>
        <span>
          {t('dbhost.openFiles', 'Otevřené soubory:')}{' '}
          <span className='text-white'>{s.open_files}</span>
        </span>
        <span>
          {t('dbhost.openTables', 'Otevřené tabulky:')}{' '}
          <span className='text-white'>{s.open_tables}</span>
        </span>
      </div>
    </div>
  );

  // helper used inside render — needs access to s.uptime_seconds
  function uptime() {
    return s.uptime_seconds;
  }
}

function WarnRow(
  { icon: Icon, children }: {
    icon: React.ComponentType<{ size?: number | string; className?: string }>;
    children: React.ReactNode;
  },
) {
  return (
    <div className='flex items-start gap-2 bg-amber-500/5 border border-amber-500/20 rounded-xl px-3 py-2 text-xs text-amber-300'>
      <Icon size={12} className='mt-0.5 flex-shrink-0 text-amber-400' />
      <span>{children}</span>
    </div>
  );
}

// ─── Process List tab ─────────────────────────────────────────────────────────

function ProcessPanel({ instance, targetNode }: { instance: string; targetNode?: string }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const extra = targetNode ? { target_node: targetNode } : {};
  const [includeSleep, setIncludeSleep] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [minTime, setMinTime] = useState(0);

  const { data, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ['mariadb-procs', instance, targetNode, includeSleep, minTime],
    queryFn: () =>
      mcpCall<{ ok: boolean; processes: MariaProcess[]; running: number; slow: number }>(
        'dbhost.mariadb_processlist', { instance, include_sleep: includeSleep, min_time: minTime, ...extra },
      ),
    refetchInterval: autoRefresh ? 3_000 : false,
  });

  const killMut = useMutation({
    mutationFn: (id: number) =>
      mcpCall('dbhost.mariadb_kill', { instance, process_id: id, kill_type: 'QUERY', ...extra }),
    onSuccess: (_, id) => {
      toast.success(t('dbhost.processKilled', 'Proces {{id}} ukončen', { id }));
      qc.invalidateQueries({ queryKey: ['mariadb-procs', instance] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const procs = data?.processes ?? [];
  const lastUpdate = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString(i18n.language)
    : null;

  function timeColor(t: number) {
    if (t >= 30) return 'text-red-400';
    if (t >= 10) return 'text-amber-400';
    if (t >= 3) return 'text-yellow-400';
    return 'text-gray-400';
  }

  return (
    <div className='space-y-3 mt-2'>
      {/* Toolbar */}
      <div className='flex items-center gap-2 flex-wrap'>
        <span className='text-xs text-gray-600'>
          {lastUpdate && t('dbhost.updatedAt', 'Aktualizováno: {{time}}', { time: lastUpdate })}
        </span>
        <div className='ml-auto flex items-center gap-2'>
          <label className='flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer'>
            <input
              type='checkbox'
              checked={includeSleep}
              onChange={(e) => setIncludeSleep(e.target.checked)}
              className='accent-blue-500'
            />
            Sleep
          </label>
          <label className='flex items-center gap-1.5 text-xs text-gray-500'>
            Min time:
            <Select
              value={minTime}
              onChange={(e) => setMinTime(Number(e.target.value))}
            >
              {[0, 1, 5, 10, 30].map((v) => <option key={v} value={v}>{v}s</option>)}
            </Select>
          </label>
          <button
            type='button'
            onClick={() => setAutoRefresh((a) => !a)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs ${
              autoRefresh
                ? 'bg-blue-600/20 text-blue-400 border-blue-500/30'
                : 'bg-gray-800 text-gray-500 border-gray-700'
            }`}
          >
            <Activity size={11} />3s
          </button>
        </div>
      </div>

      {/* Stats */}
      {data?.ok && (
        <div className='flex gap-4 text-xs text-gray-500'>
          <span>
            {t('dbhost.processesLabel', 'Procesy:')} <span className='text-white'>{procs.length}</span>
          </span>
          <span>
            {t('dbhost.activeLabel', 'Aktivní:')} <span className='text-white'>{data.running}</span>
          </span>
          <span>
            {t('dbhost.slowLabel', 'Pomalé (>5s):')}{' '}
            <span className={data.slow > 0 ? 'text-amber-400' : 'text-white'}>{data.slow}</span>
          </span>
        </div>
      )}

      {/* Table */}
      {isLoading && (
        <div className='py-8 text-center text-gray-500 text-sm'>
          <Loader2 size={14} className='animate-spin inline mr-1' />{t('common.loading', 'Načítám…')}
        </div>
      )}

      {!isLoading && procs.length === 0 && (
        <div className='py-10 text-center'>
          <CheckCircle2 size={24} className='text-emerald-400 mx-auto mb-2' />
          <p className='text-sm text-gray-400'>{t('dbhost.noActiveProcesses', 'Žádné aktivní procesy')}</p>
        </div>
      )}

      {procs.length > 0 && (
        <div className='border border-gray-800 rounded-xl overflow-hidden'>
          <table className='w-full text-xs'>
            <thead className='border-b border-gray-800 bg-gray-900/40'>
              <tr>
                <th className='text-left px-3 py-2 text-gray-600'>ID</th>
                <th className='text-left px-3 py-2 text-gray-600'>{t('dbhost.userCol', 'Uživatel')}</th>
                <th className='text-left px-3 py-2 text-gray-600'>DB</th>
                <th className='text-left px-3 py-2 text-gray-600'>Cmd</th>
                <th className='text-right px-3 py-2 text-gray-600'>{t('dbhost.timeCol', 'Čas')}</th>
                <th className='text-left px-3 py-2 text-gray-600'>{t('dbhost.stateCol', 'Stav')}</th>
                <th className='text-left px-3 py-2 text-gray-600 max-w-xs'>Query</th>
                <th className='px-3 py-2 w-8'></th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-800/40'>
              {procs.map((p) => (
                <tr
                  key={p.id}
                  className={`group hover:bg-gray-800/20 ${p.time >= 10 ? 'bg-amber-950/10' : ''}`}
                >
                  <td className='px-3 py-2 font-mono text-gray-500'>{p.id}</td>
                  <td className='px-3 py-2 text-gray-300'>{p.user}</td>
                  <td className='px-3 py-2 text-gray-500 font-mono'>{p.db ?? '—'}</td>
                  <td className='px-3 py-2'>
                    <Badge variant={p.command === 'Query' ? 'blue' : 'gray'}>{p.command}</Badge>
                  </td>
                  <td
                    className={`px-3 py-2 text-right font-mono font-semibold ${timeColor(p.time)}`}
                  >
                    {p.time}s
                  </td>
                  <td className='px-3 py-2 text-gray-500 max-w-[120px] truncate'>
                    {p.state || '—'}
                  </td>
                  <td className='px-3 py-2 max-w-xs'>
                    <code
                      className='text-[10px] text-gray-400 truncate block max-w-xs'
                      title={p.info ?? ''}
                    >
                      {p.info ? p.info.slice(0, 80) + (p.info.length > 80 ? '…' : '') : '—'}
                    </code>
                  </td>
                  <td className='px-3 py-2'>
                    {p.command === 'Query' && (
                      <button
                        type='button'
                        onClick={async () =>
                          await themedConfirm(
                            t('dbhost.confirmKill', 'Ukončit proces {{id}} ({{user}})?', {
                              id: p.id,
                              user: p.user,
                            }),
                          ) && killMut.mutate(p.id)}
                        className='opacity-0 group-hover:opacity-100 p-1 text-gray-600 hover:text-red-400 transition-all'
                        title='Kill query'
                      >
                        <XCircle size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Tables tab ───────────────────────────────────────────────────────────────

type SortKey = 'total_length' | 'data_length' | 'index_length' | 'rows';

function TablesPanel({ instance, targetNode }: { instance: string; targetNode?: string }) {
  const { t } = useTranslation();
  const extra = targetNode ? { target_node: targetNode } : {};
  const [schema, setSchema] = useState('');
  const [sort, setSort] = useState<SortKey>('total_length');
  const [sortAsc, setSortAsc] = useState(false);

  const { data, isLoading, refetch: refresh } = useQuery({
    queryKey: ['mariadb-tables', instance, targetNode, schema],
    queryFn: () =>
      mcpCall<
        { ok: boolean; tables: MariaTable[]; total_data_fmt: string; total_index_fmt: string }
      >(
        'dbhost.mariadb_tables',
        { instance, schema: schema || undefined, limit: 100, ...extra },
      ),
  });

  const tables = [...(data?.tables ?? [])].sort((a, b) => {
    const v = sortAsc ? a[sort] - b[sort] : b[sort] - a[sort];
    return v;
  });

  const maxSize = tables[0]?.total_length ?? 1;

  function SortBtn({ col, label }: { col: SortKey; label: string }) {
    const active = sort === col;
    return (
      <button
        type='button'
        onClick={() => {
          if (active) setSortAsc((a) => !a);
          else {
            setSort(col);
            setSortAsc(false);
          }
        }}
        className='flex items-center gap-0.5 hover:text-white transition-colors'
      >
        {label}
        {active && (sortAsc ? <ChevronUp size={10} /> : <ChevronDown size={10} />)}
      </button>
    );
  }

  return (
    <div className='space-y-3 mt-2'>
      <div className='flex items-center gap-2'>
        <input
          value={schema}
          onChange={(e) => setSchema(e.target.value)}
          placeholder={t('dbhost.schemaFilter', 'Filtr schématu...')}
          className='bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500 w-48'
        />
        <Button variant='ghost' size='icon' onClick={() => refresh()}>
          <RefreshCw size={13} />
        </Button>
        {data?.ok && (
          <span className='ml-auto text-xs text-gray-500'>
            Data: {data.total_data_fmt} · Index: {data.total_index_fmt}
          </span>
        )}
      </div>

      {isLoading && (
        <div className='py-8 text-center text-gray-500'>
          <Loader2 size={14} className='animate-spin inline mr-1' />{t('common.loading', 'Načítám…')}
        </div>
      )}

      {!isLoading && tables.length === 0 && (
        <div className='py-10 text-center text-gray-500 text-sm'>
          {schema
            ? t('dbhost.noTablesInSchema', 'Žádné tabulky ve schématu "{{schema}}"', { schema })
            : t('dbhost.noTables', 'Žádné tabulky')}
        </div>
      )}

      {tables.length > 0 && (
        <div className='border border-gray-800 rounded-xl overflow-hidden'>
          <table className='w-full text-xs'>
            <thead className='border-b border-gray-800 bg-gray-900/40'>
              <tr className='text-gray-600'>
                <th className='text-left px-3 py-2'>{t('dbhost.schemaTable', 'Schéma.Tabulka')}</th>
                <th className='text-center px-3 py-2'>Engine</th>
                <th className='text-right px-3 py-2 cursor-pointer'>
                  <SortBtn col='rows' label={t('dbhost.rowsCol', 'Řádky')} />
                </th>
                <th className='text-right px-3 py-2 cursor-pointer'>
                  <SortBtn col='data_length' label={t('dbhost.dataCol', 'Data')} />
                </th>
                <th className='text-right px-3 py-2 cursor-pointer'>
                  <SortBtn col='index_length' label='Index' />
                </th>
                <th className='text-right px-3 py-2 cursor-pointer'>
                  <SortBtn col='total_length' label={t('dbhost.totalCol', 'Celkem')} />
                </th>
                <th className='px-3 py-2 w-24'></th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-800/40'>
              {tables.map((t) => (
                <tr key={`${t.schema}.${t.table}`} className='hover:bg-gray-800/20 group'>
                  <td className='px-3 py-2 font-mono text-gray-300'>
                    <span className='text-gray-600'>{t.schema}.</span>
                    {t.table}
                  </td>
                  <td className='px-3 py-2 text-center'>
                    <Badge
                      variant={t.engine === 'InnoDB'
                        ? 'blue'
                        : t.engine === 'MyISAM'
                        ? 'yellow'
                        : 'gray'}
                    >
                      {t.engine}
                    </Badge>
                  </td>
                  <td className='px-3 py-2 text-right text-gray-400 font-mono'>
                    {t.rows.toLocaleString()}
                  </td>
                  <td className='px-3 py-2 text-right text-gray-400'>{t.data_fmt}</td>
                  <td className='px-3 py-2 text-right text-gray-400'>{t.index_fmt}</td>
                  <td className='px-3 py-2 text-right text-white font-medium'>{t.total_fmt}</td>
                  <td className='px-3 py-2'>
                    <div className='w-16 h-1 bg-gray-800 rounded-full overflow-hidden'>
                      <div
                        className='h-full bg-blue-500/60 rounded-full'
                        style={{ width: `${Math.min(100, (t.total_length / maxSize) * 100)}%` }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Variables tab ────────────────────────────────────────────────────────────

interface MariaVars {
  ok: boolean;
  variables: {
    memory: Record<string, string>;
    connections: Record<string, string>;
    innodb: Record<string, string>;
    slow_query: Record<string, string>;
    query_cache: Record<string, string>;
    misc: Record<string, string>;
  };
}

function VariablesPanel({ instance, targetNode }: { instance: string; targetNode?: string }) {
  const { t } = useTranslation();
  const extra = targetNode ? { target_node: targetNode } : {};
  const [filter, setFilter] = useState('');

  const { data, isLoading, refetch: refresh } = useQuery({
    queryKey: ['mariadb-vars', instance, targetNode],
    queryFn: () => mcpCall<MariaVars>('dbhost.mariadb_variables', { instance, ...extra }),
  });

  const sections = data?.variables;

  function VarTable({ title, vars }: { title: string; vars: Record<string, string> }) {
    const entries = Object.entries(vars).filter(([k]) =>
      !filter || k.toLowerCase().includes(filter.toLowerCase())
    );
    if (entries.length === 0) return null;
    return (
      <div>
        <h4 className='text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2'>
          {title}
        </h4>
        <div className='border border-gray-800 rounded-xl overflow-hidden mb-4'>
          <table className='w-full text-xs'>
            <tbody className='divide-y divide-gray-800/40'>
              {entries.map(([k, v]) => (
                <tr key={k} className='hover:bg-gray-800/20'>
                  <td className='px-3 py-2 font-mono text-blue-300 w-72'>{k}</td>
                  <td className='px-3 py-2 text-white font-mono'>{v || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-3 mt-2'>
      <div className='flex items-center gap-2'>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('dbhost.varFilter', 'Filtr proměnných...')}
          className='bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500 w-56'
        />
        <Button variant='ghost' size='icon' onClick={() => refresh()}>
          <RefreshCw size={13} />
        </Button>
      </div>

      {isLoading && (
        <div className='py-8 text-center text-gray-500'>
          <Loader2 size={14} className='animate-spin inline mr-1' />{t('common.loading', 'Načítám…')}
        </div>
      )}

      {sections && (
        <div>
          <VarTable title={t('dbhost.sectionMemory', 'Paměť')} vars={sections.memory} />
          <VarTable title='Connections' vars={sections.connections} />
          <VarTable title='InnoDB' vars={sections.innodb} />
          <VarTable title='Slow Query Log' vars={sections.slow_query} />
          <VarTable title='Query Cache' vars={sections.query_cache} />
          <VarTable title={t('dbhost.sectionMisc', 'Ostatní')} vars={sections.misc} />
        </div>
      )}
    </div>
  );
}

// ─── Explain tab ──────────────────────────────────────────────────────────────

const QUALITY_COLORS: Record<string, string> = {
  good: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  ok: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  warn: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  bad: 'bg-red-500/10 text-red-400 border-red-500/20',
};

function ExplainPanel({ instance, targetNode }: { instance: string; targetNode?: string }) {
  const { t } = useTranslation();
  const extra = targetNode ? { target_node: targetNode } : {};
  const [query, setQuery] = useState('SELECT * FROM information_schema.TABLES LIMIT 10');
  const [database, setDatabase] = useState('');
  const [result, setResult] = useState<
    { plan: ExplainStep[]; warnings: string[]; total_rows_scanned: number } | null
  >(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');

  const PRESETS = [
    {
      label: 'Tables',
      sql:
        "SELECT TABLE_SCHEMA, TABLE_NAME FROM information_schema.TABLES WHERE TABLE_TYPE='BASE TABLE'",
    },
    {
      label: 'Processlist',
      sql: "SELECT * FROM information_schema.PROCESSLIST WHERE COMMAND != 'Sleep'",
    },
    {
      label: 'Status',
      sql: 'SELECT VARIABLE_NAME, VARIABLE_VALUE FROM information_schema.GLOBAL_STATUS LIMIT 20',
    },
  ];

  const run = async () => {
    if (!query.trim()) return;
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const r = await mcpCall<
        {
          ok: boolean;
          plan: ExplainStep[];
          warnings: string[];
          total_rows_scanned: number;
          error?: string;
        }
      >(
        'dbhost.mariadb_explain',
        { instance, query: query.trim(), database: database || undefined, ...extra },
      );
      if (r.ok) {
        setResult(r);
      } else {
        setError(r.error ?? t('widgets.error', 'Chyba'));
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className='space-y-4 mt-2'>
      {/* Presets */}
      <div className='flex flex-wrap gap-1.5'>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type='button'
            onClick={() => setQuery(p.sql)}
            className='px-2.5 py-1 text-xs rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:border-gray-600 font-mono transition-colors'
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className='space-y-2'>
        <textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          rows={4}
          className='w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm font-mono text-white focus:outline-none focus:border-gray-500 resize-none'
          placeholder='SELECT ...'
        />
        <div className='flex items-center gap-2'>
          <input
            value={database}
            onChange={(e) => setDatabase(e.target.value)}
            placeholder={t('dbhost.databaseOptional', 'databáze (volitelné)')}
            className='bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500 w-44'
          />
          <div className='flex-1' />
          <Button
            variant='primary'
            size='sm'
            loading={running}
            icon={<Play size={12} />}
            onClick={run}
            disabled={!query.trim()}
          >
            EXPLAIN
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className='bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-400'>
          {error}
        </div>
      )}

      {/* Warnings */}
      {result && result.warnings.length > 0 && (
        <div className='space-y-1.5'>
          {result.warnings.map((w, i) => (
            <div
              key={i}
              className='flex items-start gap-2 bg-amber-500/5 border border-amber-500/20 rounded-xl px-3 py-2 text-xs text-amber-300'
            >
              <AlertTriangle size={11} className='mt-0.5 flex-shrink-0' />
              {w}
            </div>
          ))}
        </div>
      )}

      {/* Plan */}
      {result && (
        <div>
          <div className='flex items-center gap-3 mb-2'>
            <span className='text-xs text-gray-500'>
              {t('dbhost.rowsScanned', '~{{count}} řádků celkem prohledáno', {
                count: result.total_rows_scanned.toLocaleString(),
              })}
            </span>
          </div>
          <div className='border border-gray-800 rounded-xl overflow-hidden'>
            <table className='w-full text-xs'>
              <thead className='border-b border-gray-800 bg-gray-900/40'>
                <tr className='text-gray-600 text-left'>
                  <th className='px-3 py-2'>id</th>
                  <th className='px-3 py-2'>select_type</th>
                  <th className='px-3 py-2'>table</th>
                  <th className='px-3 py-2'>type</th>
                  <th className='px-3 py-2'>key</th>
                  <th className='px-3 py-2'>key_len</th>
                  <th className='px-3 py-2 text-right'>rows</th>
                  <th className='px-3 py-2 text-right'>filtered</th>
                  <th className='px-3 py-2'>Extra</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-800/40'>
                {result.plan.map((step, i) => (
                  <tr key={i} className='hover:bg-gray-800/20'>
                    <td className='px-3 py-2 text-gray-500'>{step.id}</td>
                    <td className='px-3 py-2 text-gray-400'>{step.select_type}</td>
                    <td className='px-3 py-2 font-mono text-gray-300'>{step.table}</td>
                    <td className='px-3 py-2'>
                      <span
                        className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-semibold ${
                          QUALITY_COLORS[step.quality]
                        }`}
                      >
                        {step.type}
                      </span>
                    </td>
                    <td className='px-3 py-2 font-mono text-emerald-400'>
                      {step.key ?? <span className='text-gray-700'>—</span>}
                    </td>
                    <td className='px-3 py-2 text-gray-500'>{step.key_len ?? '—'}</td>
                    <td className='px-3 py-2 text-right font-mono text-white'>
                      {step.rows.toLocaleString()}
                    </td>
                    <td className='px-3 py-2 text-right text-gray-400'>{step.filtered}%</td>
                    <td className='px-3 py-2 text-gray-500 max-w-xs truncate' title={step.extra}>
                      {step.extra || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

interface MariaDbPerfPanelProps {
  instance: string;
  targetNode?: string;
}

export function MariaDbPerfPanel({ instance, targetNode }: MariaDbPerfPanelProps) {
  const { t } = useTranslation();
  return (
    <Tabs defaultTab='overview'>
      <TabList>
        <Tab id='overview'>
          <Activity size={12} />{t('dbhost.tabOverview', 'Přehled')}
        </Tab>
        <Tab id='processes'>
          <Zap size={12} />{t('dbhost.tabProcesses', 'Procesy')}
        </Tab>
        <Tab id='tables'>
          <Database size={12} />{t('dbhost.tables', 'Tabulky')}
        </Tab>
        <Tab id='variables'>
          <Settings size={12} />{t('dbhost.tabConfig', 'Konfigurace')}
        </Tab>
        <Tab id='explain'>
          <Terminal size={12} />EXPLAIN
        </Tab>
      </TabList>
      <TabPanel id='overview'>
        <OverviewPanel instance={instance} targetNode={targetNode} />
      </TabPanel>
      <TabPanel id='processes'>
        <ProcessPanel instance={instance} targetNode={targetNode} />
      </TabPanel>
      <TabPanel id='tables'>
        <TablesPanel instance={instance} targetNode={targetNode} />
      </TabPanel>
      <TabPanel id='variables'>
        <VariablesPanel instance={instance} targetNode={targetNode} />
      </TabPanel>
      <TabPanel id='explain'>
        <ExplainPanel instance={instance} targetNode={targetNode} />
      </TabPanel>
    </Tabs>
  );
}
